import CheckIcon from '@mui/icons-material/Check';
import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const Automatic = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Automatic Revocation of Tax-Exempt Status | IRS Rules"
          description="Understand why tax-exempt status is automatically revoked, what it means for your organization, and how to apply for reinstatement with the IRS."
          keywords=""
          canonical="resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
          ogType="article"
          jsonLdPricing="89.99"
          jsonLdAggregateRatingValue="4.99"
          jsonLdAggregateRatingCount="2063"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 3,
            mt: 5,
            textAlign: 'center',
          }}
        >
          {' '}
          <span className="highlight-container">
            <span className="highlight">Automatic Revocation </span>
          </span>{' '}
          of Tax-Exempt Status:
          <br /> What Nonprofits Need to Know
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Avoid automatic revocation by filing your IRS returns on time every
          year!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=exemptstatus`}
          >
            E-file Now!
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/tax-exempt/automatic-revocation-of-tax-exempt-status/"
                  />

                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-20T09:00:00-05:00"
                      >
                        July 20, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-20T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 20, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{
                            color: 'inherit',
                            textDecoration: 'none',
                          }}
                        >
                          Peter Samuel
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Automatic Revocation at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          What Is Automatic Revocation?{' '}
                        </strong>{' '}
                        The IRS automatically revokes an organization's{' '}
                        <strong style={{ fontWeight: 600 }}>
                          tax-exempt status
                        </strong>{' '}
                        if it fails to file a required annual return or notice
                        for{' '}
                        <strong style={{ fontWeight: 600 }}>
                          three consecutive years.
                        </strong>
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Who Is Affected?{' '}
                        </strong>{' '}
                        Most organizations are required to
                        <Link
                          href="https://www.taxzerone.com/tax-exempt/e-file-irs-form-990/"
                          sx={{
                            color: '#0202ff',
                            textDecoration: 'underline',
                          }}
                        >
                          {' '}
                          file Form 990
                        </Link>
                        ,{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-EZ, Form 990-N (e-Postcard),
                        </strong>{' '}
                        or{' '}
                        <strong style={{ fontWeight: 600 }}>
                          Form 990-PF.
                        </strong>
                        Churches and certain church-related organizations are
                        generally exempt from this requirement.
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          How to Check Your Status:{' '}
                        </strong>{' '}
                        Verify your organization's status through the{' '}
                        <strong style={{ fontWeight: 600 }}>
                          IRS Automatic Revocation List{' '}
                        </strong>{' '}
                        or by reviewing the IRS revocation notice (CP120A).
                      </span>
                    </li>

                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Can Tax-Exempt Status Be Restored?{' '}
                        </strong>{' '}
                        Yes. Eligible organizations can apply for{' '}
                        <strong style={{ fontWeight: 600 }}>
                          reinstatement
                        </strong>{' '}
                        by submitting the appropriate IRS application, required
                        annual returns, and applicable user fee.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>

                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Nonprofit organizations can lose their tax-exempt status if
                  they don’t file the required annual tax forms, such as{' '}
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    {' '}
                    Form 990 series
                  </Link>
                  , as this may lead to{' '}
                  <span style={{ fontWeight: 600 }}> automatic revocation</span>
                  . This means the organization will no longer be exempt from
                  federal income tax and may also face penalties.
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  This resource page will help you understand when automatic
                  revocation occurs, how to check your status, and what steps to
                  take if your exemption has been revoked.
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <strong style={{ fontWeight: 600 }}>
                      Table of Contents{' '}
                    </strong>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#when" sx={{ color: '#0202ff' }}>
                          When does Automatic Revocation occur?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#conseq" sx={{ color: '#0202ff' }}>
                          Consequences of Automatic Revocation
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#how" sx={{ color: '#0202ff' }}>
                          How to check if an organization has been Revoked?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#tax" sx={{ color: '#0202ff' }}>
                          Reinstating Tax-Exempt status
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#what" sx={{ color: '#0202ff' }}>
                          What Happens After the IRS Approves Your
                          Reinstatement?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#best" sx={{ color: '#0202ff' }}>
                          Best practices to avoid revocation
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="when"
                >
                  When does Automatic Revocation Occur?
                </Typography>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Automatic revocation occurs when a tax-exempt organization
                  fails to file the required IRS annual return or notice (
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>
                  ,{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-EZ
                  </Link>
                  ,{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=exemptstatus`}
                    sx={{ color: '#0202ff' }}
                  >
                    990-N
                  </Link>
                  , or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>
                  ) for{' '}
                  <span style={{ fontWeight: 600 }}>
                    {' '}
                    three consecutive years
                  </span>
                  . This rule applies to most tax-exempt organizations, except
                  for churches and certain church-related entities that are not
                  required to file.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="conseq"
                >
                  Consequences of Automatic Revocation
                </Typography>
                <Box component="ul" sx={{ mb: 4, '& li': { mb: 1.2 } }}>
                  <li>
                    The organization{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      loses its federal tax-exempt status
                    </span>
                    .
                  </li>
                  <li>
                    It may need to file Form 1120 (U.S. Corporation Income Tax
                    Return) or Form 1041 (U.S. Income Tax Return for Estates and
                    Trusts).
                  </li>
                  <li>
                    It is{' '}
                    <span style={{ fontWeight: 600 }}>
                      no longer eligible to receive tax-deductible contributions
                    </span>
                    .
                  </li>
                  <li>
                    It is{' '}
                    <span style={{ fontWeight: 600 }}>
                      removed from IRS Publication 78
                    </span>{' '}
                    (the list of eligible charities).
                  </li>
                  <li>
                    The IRS will send a{' '}
                    <span style={{ fontWeight: 600 }}>
                      revocation notice letter
                    </span>
                    .
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      State and local laws
                    </span>{' '}
                    may impose additional consequences.{' '}
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="how"
                >
                  How to Check if an Organization Has Been Revoked?
                </Typography>

                <Typography variant="body1" sx={{ mb: 2 }}>
                  There are two ways to check if an organization’s tax-exempt
                  status has been revoked.
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  Notice:{' '}
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS sends a{' '}
                  <span style={{ fontWeight: 600 }}> CP120A notice</span>
                  &nbsp;to the organization&apos;s last known address informing
                  them that their tax-exempt status has been{' '}
                  <span style={{ fontWeight: 600 }}>revoked</span> due to
                  failure to file the required annual returns (
                  <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                    Form 990 series
                  </Link>
                  ) notices for{' '}
                  <span style={{ fontWeight: 600 }}>
                    three consecutive years
                  </span>
                  .
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  The Revocation List:
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The IRS publishes a monthly updated{' '}
                  <span style={{ fontWeight: 600 }}>
                    Automatic Revocation of Exemption List
                  </span>{' '}
                  on its website, which includes:
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Organization’s name </li>
                  <li>Employer Identification Number (EIN) </li>
                  <li>Organization’s type </li>
                  <li>Last known address </li>
                  <li>Effective date of revocation </li>
                  <li>Date added to the revocation list </li>
                </Box>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  You can search this list to verify if an organization’s
                  tax-exempt status has been revoked on the{' '}
                  <Link
                    href="https://apps.irs.gov/app/eos/"
                    target="_blank"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    IRS website
                  </Link>
                  .
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="tax"
                >
                  Reinstating Tax-Exempt Status
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Organizations must apply for{' '}
                  <span style={{ fontWeight: 600 }}>reinstatement</span> and pay
                  a user fee, even if they were not originally required to apply
                  for exemption.{' '}
                </Typography>
                <Typography variant="body1" sx={{ fontWeight: 600 }}>
                  Four Ways to Apply for Reinstatement (per Revenue Procedure
                  2014-11):{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>Streamlined Retroactive Reinstatement </li>
                  <li>Retroactive Reinstatement (within 15 months) </li>
                  <li>Retroactive Reinstatement (after 15 months) </li>
                  <li>Post-Mark Date Reinstatement </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  1. Streamlined Retroactive Reinstatement:
                </Typography>
                <Typography variant="body1" sx={{}}>
                  For organizations eligible to file{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-ez/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990-EZ
                  </Link>{' '}
                  or{' '}
                  <Link
                    href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=exemptstatus`}
                    sx={{ color: '#0202ff' }}
                  >
                    990-N
                  </Link>{' '}
                  for the three years causing revocation and that have never
                  been revoked before.
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Submit Form <span style={{ fontWeight: 600 }}>1023</span>,{' '}
                    <span style={{ fontWeight: 600 }}>1023-EZ</span>,{' '}
                    <span style={{ fontWeight: 600 }}>1024</span>, or{' '}
                    <span style={{ fontWeight: 600 }}>1024-A</span> &nbsp;with
                    user fee no later than 15 months after the later of the date
                    of the organization&apos;s Revocation Letter (CP-120A) or
                    the date the organization appeared on the Revocation
                    List.{' '}
                  </li>
                  <li>
                    File all required past returns ({' '}
                    <span style={{ fontWeight: 600 }}>990-EZ or 990-N</span>)
                    for the three years that caused revocation.{' '}
                  </li>
                  <li>
                    No penalty for late filing if reinstated retroactively.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  2. Retroactive Reinstatement (Within 15 Months)
                </Typography>
                <Typography variant="body1" sx={{}}>
                  For organizations that do not qualify for streamlined
                  reinstatement (e.g., required to file <br />
                  <Link
                    href="/tax-exempt/e-file-irs-form-990/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    Form 990
                  </Link>{' '}
                  or{' '}
                  <Link
                    href="/tax-exempt/e-file-irs-form-990-pf/"
                    sx={{ color: '#0202ff' }}
                  >
                    990-PF
                  </Link>{' '}
                  or previously revoked).
                </Typography>
                <Box component="ul" sx={{ mb: 2, '& li': { mb: 1.2 } }}>
                  <li>
                    Submit Form <span style={{ fontWeight: 600 }}>1023</span>,{' '}
                    <span style={{ fontWeight: 600 }}>1024</span>, or{' '}
                    <span style={{ fontWeight: 600 }}>1024-A</span>&nbsp;with
                    user fee not later than 15 months after the later of the
                    date on the organization&apos;s revocation letter (CP-120A)
                    or the date the organization appeared on the Revocation
                    List.
                  </li>
                  <li>
                    Include a statement of reasonable cause for failure to file
                    at least one of the three years.
                  </li>
                  <li>
                    File all required returns for the three years and any
                    subsequent years.
                  </li>
                  <li>
                    Write{' '}
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      &quot;Retroactive Reinstatement&quot;
                    </span>{' '}
                    on returns submitted.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  3. Retroactive Reinstatement (After 15 Months)
                </Typography>
                <Typography variant="body1" sx={{}}>
                  For organizations applying more than 15 months after
                  revocation notice or listing date.{' '}
                </Typography>
                <Box component="ul" sx={{ '& li': { mb: 1.2 } }}>
                  <li>
                    Must establish reasonable cause for failure to file for{' '}
                    <span style={{ fontWeight: 600 }}>
                      all three consecutive years
                    </span>{' '}
                    in which it failed to file.{' '}
                  </li>
                </Box>
                <Typography variant="body1" sx={{ fontWeight: 600, mb: 1 }}>
                  4. Post-Mark Date Reinstatement
                </Typography>
                <Typography variant="body1" sx={{}}>
                  Organizations may apply for reinstatement effective from the
                  date the IRS receives their application if they do not qualify
                  for retroactive reinstatement.
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    Submit{' '}
                    <span style={{ fontWeight: 600 }}>
                      Form 1023, 1023-EZ, 1024,
                    </span>{' '}
                    or <span style={{ fontWeight: 600 }}>1024-A </span> with the
                    user fee.
                  </li>
                  <li>
                    Check the appropriate box on the application indicating
                    <span style={{ fontWeight: 600 }}>
                      {' '}
                      post-mark date reinstatement.
                    </span>{' '}
                  </li>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="what"
                >
                  What Happens After the IRS Approves Your Reinstatement?
                </Typography>
                <Typography variant="body1">
                  Once the IRS approves your organization’s application for
                  reinstatement of tax-exempt status,{' '}
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.2 } }}>
                  <li>
                    The organization will receive a{' '}
                    <span style={{ fontWeight: 600 }}>
                      new determination letter
                    </span>
                    . This letter confirms that your organization is once again
                    in good standing with the IRS.
                  </li>
                  <li>
                    The IRS also will include the reinstated organization in the
                    next update of{' '}
                    <span style={{ fontWeight: 600 }}>
                      Tax-Exempt Organizations Search
                    </span>{' '}
                    (<span style={{ fontWeight: 600 }}>Pub. 78</span>
                    database)
                  </li>
                  <li>
                    It also indicates in the IRS{' '}
                    <span style={{ fontWeight: 600 }}>
                      Business Master File
                    </span>{' '}
                    (<span style={{ fontWeight: 600 }}>BMF</span>) extract that
                    the organization is eligible to receive tax-deductible
                    contributions.{' '}
                  </li>
                </Box>

                <Box
                  sx={{
                    textAlign: 'center',
                    p: 2,
                    borderRadius: 2,
                    backgroundColor: '#f9f9f9',
                    mb: 4,
                    border: '1px solid #ccc',
                  }}
                >
                  <Typography
                    variant="h3"
                    sx={{
                      fontSize: { lg: 24, md: 20, sm: 18, xs: 16 },
                      fontWeight: 600,
                      mb: 0.5,
                      textAlign: 'left',
                    }}
                  >
                    š ï¸ Caution:
                  </Typography>
                  <Typography variant="body1" sx={{ mb: 0, textAlign: 'left' }}>
                    If your organization was mistakenly listed as automatically
                    revoked, you can contact the IRS Tax Exempt and Government
                    Entities Customer Service at{' '}
                    <span style={{ fontWeight: 600 }}> (877) 829-5500</span> for
                    further assistance and to resolve the issue.
                  </Typography>

                  {/* <Button
                    variant="contained"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      boxShadow: 'none',
                      borderRadius: 2,
                    }}
                    size="large"
                    href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=990duedates`}
                  >
                    File 8868 Now
                  </Button> */}
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 20, sm: 18, xs: 16 },
                    fontWeight: 600,
                    mb: 0.5,
                    scrollMarginTop: 83,
                  }}
                  id="best"
                >
                  Best Practices to Avoid Automatic Revocation
                </Typography>
                <Box
                  component="ul"
                  sx={{
                    mb: 4,

                    listStyleType: 'none',
                    '& li': { mb: 1.2 },
                  }}
                >
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Filing the appropriate{' '}
                    <Link href="/tax-exempt/" sx={{ color: '#0202ff' }}>
                      {' '}
                      Form 990 series
                    </Link>{' '}
                    annually and timely is critical to maintaining tax-exempt
                    status and avoiding{' '}
                    <span style={{ fontWeight: 600 }}>
                      automatic revocation
                    </span>
                    .{' '}
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Small organizations with gross receipts normally under
                    <span style={{ fontWeight: 600 }}> $50,000 </span> may file
                    <Link
                      href={`${process.env.NEXT_PUBLIC_990N_SITE_URL}?ref=exemptstatus`}
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      Form 990-N (e-Postcard)
                    </Link>
                    , but those exceeding this threshold must file{' '}
                    <Link
                      href="/tax-exempt/e-file-irs-form-990/"
                      sx={{ color: '#0202ff' }}
                    >
                      Form 990
                    </Link>{' '}
                    or
                    <Link
                      href="/tax-exempt/e-file-irs-form-990-ez/"
                      sx={{ color: '#0202ff' }}
                    >
                      {' '}
                      990-EZ
                    </Link>
                    .
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Religious organizations are generally exempt from filing
                    these annual returns and thus not subject to automatic
                    revocation.
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Organizations should keep copies and records of all filings
                    to avoid mistaken revocations and to support reinstatement
                    applications if needed.
                  </li>
                  <li style={{}}>
                    {' '}
                    <CheckIcon sx={{ color: '#1da94a', mr: 1, mb: -0.5 }} />
                    Consulting a tax professional is advisable to navigate the
                    reinstatement process effectively
                  </li>
                </Box>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The above given information is based on the IRS
                        guidelines. For more details, please refer to the
                        <Link
                          href="https://www.irs.gov/charities-non-profits/automatic-revocation-of-exemption"
                          target="_blank"
                          sx={{ color: '#0202ff' }}
                        >
                          {' '}
                          automatic revocation of exemption{' '}
                        </Link>{' '}
                        in IRS.
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>
      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 3,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Protect or Reinstate Your Tax-Exempt Status
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                fontWeight: 400,
                color: '#333333',
                mb: 2,
              }}
            >
              Stay compliant and protect your mission, review your filing
              requirements and
              <br /> submit your return today!
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=exemptstatus`}
              >
                E-file Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default Automatic;
